import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { AlertService } from 'src/app/services/auth';
import { CacheWarmupService } from 'src/app/services/offline/cache-warmup.service';
import { SyncService } from 'src/app/services/offline/sync.service';
import { MenuService } from 'src/app/services/menu/menu-service';
import { Order } from 'src/app/shared/models/orders';
import { HormigonDispatchFacade } from '../despacho/hormigon-dispatch.facade';
import {
  classifyHormigonDispatches,
  HormigonDispatchSelectors,
} from '../despacho/hormigon-dispatch-selector';

@Component({
  selector: 'app-hormigon-otros-despachos',
  templateUrl: './hormigon-otros-despachos.component.html',
  styleUrls: ['./hormigon-otros-despachos.component.css'],
  standalone: false,
})
export class HormigonOtrosDespachosComponent implements OnInit, OnDestroy {
  otherOrders: Order[] = [];

  private readonly destroy$ = new Subject<void>();
  private readonly loadOrders$ = new Subject<boolean>();
  private ignoreWarmupOrdersUpdateUntil = 0;
  private lastConflictSignature = '';

  private readonly dispatchSelectors: HormigonDispatchSelectors<Order> = {
    deliveryDate: (order) => order.deliveryDate,
    status: (order) => order.status,
    flagPending: (order) => order.flag_pending,
    startTime: (order) => order.startTime,
    identifier: (order) => order.tkt,
  };

  constructor(
    private readonly menu: MenuService,
    private readonly dispatchFacade: HormigonDispatchFacade,
    private readonly cacheWarmup: CacheWarmupService,
    private readonly syncService: SyncService,
    private readonly alertService: AlertService,
    private readonly router: Router,
  ) {
    menu.btnPrevius = true;
    menu.tilteMenu = 'Otros despachos';
  }

  onContinue(order: Order): void {
    this.router.navigate(['/otrosdespachos/detalle'], {
      queryParams: { tkt: order.tkt, paso: 'observacion' },
    });
  }

  onPaperGuide(order: Order): void {
    this.router.navigate(['/otrosdespachos/detalle'], {
      queryParams: { tkt: order.tkt, mode: 'papel' },
    });
  }

  ngOnInit(): void {
    this.loadOrders$
      .pipe(
        debounceTime(80),
        switchMap((forceNetwork) => this.dispatchFacade.list(forceNetwork)),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => this.applyOrdersFilter(data));

    this.cacheWarmup.ordersUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (Date.now() < this.ignoreWarmupOrdersUpdateUntil) return;
        this.loadOrders$.next(false);
      });

    this.loadOrders$.next(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateUrl(): void {
    this.ignoreWarmupOrdersUpdateUntil = Date.now() + 4000;
    this.syncService.flushNow();
    this.loadOrders$.next(true);
  }

  private applyOrdersFilter(data: Order[]): void {
    const selection = classifyHormigonDispatches(
      data || [],
      this.dispatchSelectors,
    );
    this.otherOrders = selection.others;
    this.notifyCurrentConflicts(selection.currentConflicts);
  }

  private notifyCurrentConflicts(conflicts: Order[]): void {
    if (!conflicts.length) {
      this.lastConflictSignature = '';
      return;
    }

    const signature = conflicts
      .map((order) => String(order.tkt))
      .sort()
      .join(',');
    if (signature === this.lastConflictSignature) return;

    this.lastConflictSignature = signature;
    this.alertService.warn(
      `Se detectaron varios viajes en curso para hoy. Tickets adicionales: ${signature}.`,
    );
  }
}
